import * as React from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { Box, TextInput, Switch, Group, Button, createStyles, Text } from '@mantine/core';
import { useForm } from "@mantine/form";
import axios from 'axios';
import type { IResponse } from "../../types/api";
import { showMessage } from "../../lib/notification";
import { CLIENT_ROUTE, API_ROUTE, CLIENT_TITLE } from "./constants";
import { Ebarimt, IUserSubscription, PaymentType } from "./types";
import produce from "immer";
import { paymentTypes } from "./constants";
import Page from "../../components/layout/Page";
import { useFormStyles } from "../../styles/form";
import { openConfirmModal } from '@mantine/modals';

const useTableStyles = createStyles((theme) => ({
    table: {
        border: '1px solid',
        borderColor: theme.colors.gray[4],
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '14px',
        margin: '1rem 0',
        'th, td': {
            textAlign: 'left',
            border: '1px solid',
            borderColor: theme.colors.gray[4],
            padding: '.3rem 1rem'
        },
    }
}));
interface CancelResponse {
    data: {
        _id: string;
    }
    id: string;
}

interface FormValues extends Pick<IUserSubscription, 'userId' | 'subscriptionId' | 'startDate' | 'endDate' | 'orderId' | 'createdUserName' | 'isActive'> {
    paidBy: PaymentType | '';
}

const EditUserSubscription = () => {

    const { state: id = null } = useLocation();

    const [ebarimt, setEbarimt] = React.useState<Ebarimt | null>(null);

    const { classes } = useFormStyles();
    const { classes: tableClasses } = useTableStyles();
    const navigate = useNavigate();

    const form = useForm<FormValues>({
        initialValues: {
            createdUserName: "",
            endDate: "",
            isActive: false,
            orderId: "",
            paidBy: "",
            startDate: "",
            subscriptionId: "",
            userId: ""
        },
    });

    const fetchData = async () => {
        const { data } = await axios.get<IResponse<IUserSubscription>>(`${API_ROUTE}/${id}`);
        setEbarimt(data.result.order?.ebarimt);
        form.setValues(
            produce(data.result, draft => {
                // @ts-ignore
                draft.paidBy = paymentTypes?.[draft?.paidBy] ?? draft.paidBy;
            })
        );
        form.resetDirty();
    }

    React.useEffect(() => {
        void fetchData();
    }, []);

    const handleSubmit = async (values: FormValues) => {
        const { data } = await axios.post<IResponse<any>>(`${API_ROUTE}/${id}`, values);
        if (data.statusCode === 200) {
            navigate(CLIENT_ROUTE);
            showMessage('Амжилттай', 'green')
        } else {
            showMessage('Алдаа гарлаа')
        }
    }

    const handleCancelConfirm = async () => {
        const response = await axios.post<IResponse<CancelResponse>>(`/api/user-subscription/${id}/cancel`);
        if (response?.data?.statusCode === 200) {
            showMessage('Амжилттай', 'green');
            void fetchData();
        }
    }

    const handleCancel = () => {
        openConfirmModal({
            title: 'Худалдан авалт буцаах',
            children: 'Та уг худалдан авалтыг буцаахдаа итгэлтэй байна уу?',
            labels: {
                confirm: 'Буцаах',
                cancel: 'Хаах'
            },
            onConfirm: () => {
                void handleCancelConfirm();
            },
            confirmProps: {
                color: 'red'
            }
        });
    }

    return (
        <Page>
            <Page.Header title={`${CLIENT_TITLE}`} backRoute={CLIENT_ROUTE}>
                <Button onClick={handleCancel} variant='outline' color='red'>
                    Буцаах
                </Button>
            </Page.Header>
            <Page.Body>
                {
                    ebarimt && ( 
                        <Box mx='auto' sx={{ maxWidth: 600, minWidth: 400 }}>
                            <Text fw={500} fz={18}>
                                EBarimt
                            </Text>
                            <table className={tableClasses.table}>
                                <tbody>
                                    <tr>
                                        <td>
                                            Төлөв
                                        </td>
                                        <th>
                                            {ebarimt?.status}
                                        </th>
                                    </tr>
                                    <tr>
                                        <td>
                                            ID
                                        </td>
                                        <th>
                                            {ebarimt?.id}
                                        </th>
                                    </tr>
                                    <tr>
                                        <td>
                                            Огноо
                                        </td>
                                        <th>
                                            {ebarimt?.date}
                                        </th>
                                    </tr>
                                    <tr>
                                        <td>
                                            Сугалааны дугаар
                                        </td>
                                        <th>
                                            {ebarimt?.lotteryNumber}
                                        </th>
                                    </tr>
                                </tbody>
                            </table>
                        </Box>
                    )
                }
                <Box sx={{ maxWidth: 500 }} mx="auto">

                    <form onSubmit={form.onSubmit(handleSubmit)} className={classes.form}>
                        <TextInput
                            withAsterisk
                            label="Хэрэглэгч"
                            disabled
                            {...form.getInputProps('userId')}
                        />

                        <TextInput
                            withAsterisk
                            disabled
                            label="Багц"
                            {...form.getInputProps('subscriptionId')}
                        />

                        <TextInput
                            withAsterisk
                            disabled
                            label="Эхлэх өдөр"
                            {...form.getInputProps('startDate')}
                        />

                        <TextInput
                            withAsterisk
                            label="Дуусах өдөр"
                            disabled
                            {...form.getInputProps('endDate')}
                        />

                        <TextInput
                            withAsterisk
                            disabled
                            label="Нэхэмжлэх дугаар"
                            {...form.getInputProps('orderId')}
                        />

                        <TextInput
                            withAsterisk
                            label="Төлбөр төлсөн хэрэгсэл"
                            disabled
                            {...form.getInputProps('paidBy')}
                        />

                        <TextInput
                            withAsterisk
                            disabled
                            label="Үүсгэсэн хэрэглэгч нэр"
                            {...form.getInputProps('createdUserName')}
                        />

                        <Switch
                            label="Идэвхтэй эсэх"
                            {...form.getInputProps('isActive', { type: 'checkbox' })}
                        />
                    </form>
                </Box>
            </Page.Body>
            <Page.Footer>
                <Group position="right" mt="md">
                    <Button color='color-secondary' onClick={() => form.onSubmit(handleSubmit)()}>Хадгалах</Button>
                </Group>
            </Page.Footer>
        </Page>
    );
};

export default EditUserSubscription;